import { useState, useRef, useEffect } from 'react';

export default function Testimonials() {
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [currentX, setCurrentX] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const testimonials = [
    {
      quote: "The aviation medical training we received was exceptional. Within three months of completing the program, our crew successfully managed a cardiac emergency at 41,000 feet. The passenger survived because of the skills and confidence this training provided.",
      author: "Captain James Morrison",
      role: "Chief Pilot, Private Aviation Fleet",
      company: "Global Executive Aviation",
      image: "https://public.readdy.ai/ai/img_res/b02807e68715aadb6403b81ceaa79361.jpg",
      rating: 5
    },
    {
      quote: "U First Doctors transformed our yacht crew's medical readiness. Their maritime-specific training gave us the skills to handle a serious injury 200 miles offshore. The evacuation coordination protocols they taught us saved a crew member's life.",
      author: "Captain Sarah Chen",
      role: "Master, 180ft Superyacht",
      company: "Mediterranean Yacht Operations",
      image: "https://readdy.ai/api/search-image?query=professional-confident-female-yacht-captain-in-elegant-white-maritime-uniform-standing-on-luxury-superyacht-bridge-experienced-maritime-professional-sophisticated-yacht-interior-natural-lighting-executive-yachting-setting&width=400&height=400&seq=testimonial002&orientation=squarish",
      rating: 5
    },
    {
      quote: "As head of security for a UHNW family, I've worked with many training providers. U First Doctors stands apart. Their tactical medical training integrated seamlessly with our protection protocols and elevated our entire team's capability.",
      author: "Marcus Rodriguez",
      role: "Director of Security",
      company: "Private Family Office",
      image: "https://readdy.ai/api/search-image?query=professional-confident-male-security-director-in-elegant-dark-business-suit-standing-in-luxury-estate-interior-experienced-executive-protection-professional-sophisticated-environment-natural-lighting-high-end-security-setting&width=400&height=400&seq=testimonial003&orientation=squarish",
      rating: 5
    },
    {
      quote: "Our estate staff completed the medical training program, and it's been transformative. Last month, our head housekeeper used her AED training to save a guest's life. The discretion and professionalism taught in this program is unmatched.",
      author: "Elizabeth Thornton",
      role: "Estate Manager",
      company: "Belmont Estate",
      image: "https://readdy.ai/api/search-image?query=professional-confident-female-estate-manager-in-elegant-business-attire-standing-in-luxury-private-residence-interior-experienced-household-management-professional-sophisticated-estate-environment-natural-lighting-high-end-setting&width=400&height=400&seq=testimonial004&orientation=squarish",
      rating: 5
    },
    {
      quote: "The custom training program U First Doctors designed for our hotel exceeded all expectations. Our staff now handles VIP guest medical situations with the same discretion and excellence we apply to every aspect of our service.",
      author: "Philippe Dubois",
      role: "General Manager",
      company: "Le Grand Château Resort",
      image: "https://readdy.ai/api/search-image?query=professional-confident-male-hotel-general-manager-in-elegant-formal-business-suit-standing-in-luxury-five-star-hotel-lobby-experienced-hospitality-professional-sophisticated-hotel-interior-natural-lighting-high-end-hospitality-setting&width=400&height=400&seq=testimonial005&orientation=squarish",
      rating: 5
    },
    {
      quote: "We've trained with U First Doctors for three years now. Their scenario-based approach and real-world focus means our crew is genuinely prepared. The annual recertification keeps our skills sharp and our confidence high.",
      author: "Commander David Walsh",
      role: "Head of Maritime Operations",
      company: "Atlantic Charter Fleet",
      image: "https://readdy.ai/api/search-image?query=professional-confident-male-maritime-commander-in-formal-white-naval-uniform-standing-on-luxury-yacht-deck-experienced-maritime-operations-professional-sophisticated-yacht-setting-natural-lighting-executive-maritime-environment&width=400&height=400&seq=testimonial006&orientation=squarish",
      rating: 5
    },
    {
      quote: "When our corporate jet experienced a medical emergency during an international flight, our crew's training from U First Doctors made all the difference. They stabilized the situation professionally and coordinated with ground medical teams flawlessly. This training is now mandatory for all our flight crews.",
      author: "Jennifer Blackwood",
      role: "VP of Flight Operations",
      company: "Apex Corporate Aviation",
      image: "https://readdy.ai/api/search-image?query=professional-confident-female-aviation-executive-in-elegant-navy-business-suit-standing-in-modern-luxury-corporate-jet-hangar-experienced-flight-operations-leader-sophisticated-aviation-facility-natural-lighting-executive-corporate-aviation-environment&width=400&height=400&seq=testimonial007&orientation=squarish",
      rating: 5
    },
    {
      quote: "As a private island resort manager, having medical expertise on-site is critical. U First Doctors trained our entire team to handle everything from minor injuries to serious emergencies. Their remote location protocols have given our guests and staff incredible peace of mind.",
      author: "Alessandro Marino",
      role: "Resort Director",
      company: "Isola Privata Resort",
      image: "https://readdy.ai/api/search-image?query=professional-confident-male-resort-director-in-elegant-white-linen-suit-standing-in-luxury-tropical-island-resort-lobby-experienced-hospitality-executive-sophisticated-beachfront-resort-interior-natural-lighting-exclusive-island-resort-setting&width=400&height=400&seq=testimonial008&orientation=squarish",
      rating: 5
    }
  ];

  const handleTouchStart = (e: React.TouchEvent) => {
    setIsDragging(true);
    setStartX(e.touches[0].clientX);
    setCurrentX(e.touches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    setCurrentX(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    
    const diff = startX - currentX;
    const threshold = 50;

    if (Math.abs(diff) > threshold) {
      if (diff > 0 && activeTestimonial < testimonials.length - 1) {
        setActiveTestimonial(activeTestimonial + 1);
      } else if (diff < 0 && activeTestimonial > 0) {
        setActiveTestimonial(activeTestimonial - 1);
      }
    }

    setIsDragging(false);
    setStartX(0);
    setCurrentX(0);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setStartX(e.clientX);
    setCurrentX(e.clientX);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    setCurrentX(e.clientX);
  };

  const handleMouseUp = () => {
    if (!isDragging) return;
    
    const diff = startX - currentX;
    const threshold = 50;

    if (Math.abs(diff) > threshold) {
      if (diff > 0 && activeTestimonial < testimonials.length - 1) {
        setActiveTestimonial(activeTestimonial + 1);
      } else if (diff < 0 && activeTestimonial > 0) {
        setActiveTestimonial(activeTestimonial - 1);
      }
    }

    setIsDragging(false);
    setStartX(0);
    setCurrentX(0);
  };

  const handleMouseLeave = () => {
    if (isDragging) {
      setIsDragging(false);
      setStartX(0);
      setCurrentX(0);
    }
  };

  return (
    <section className="bg-black py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-20">
          <div className="text-[9px] uppercase tracking-[0.2em] text-[#707070] font-light mb-4">
            Client Testimonials
          </div>
          <h2 className="text-[40px] sm:text-[48px] md:text-[56px] font-bold text-[#F5F5F5] mb-6 font-serif">
            Trusted By The Best
          </h2>
          <p className="text-[16px] text-[#9A9A9A] max-w-[800px] mx-auto leading-relaxed">
            Our training programs have earned the trust of aviation professionals, maritime crews, security teams, and estate managers worldwide.
          </p>
        </div>

        <div className="relative">
          <div 
            ref={containerRef}
            className="bg-[#0D0D0D] border border-[#1A1A1A] rounded-2xl p-8 md:p-12 lg:p-16 cursor-grab active:cursor-grabbing select-none"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
          >
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-center">
              <div className="lg:col-span-1">
                <div className="relative w-full max-w-[300px] mx-auto">
                  <div className="aspect-square rounded-2xl overflow-hidden">
                    <img 
                      src={testimonials[activeTestimonial].image}
                      alt={testimonials[activeTestimonial].author}
                      className="w-full h-full object-cover"
                      draggable="false"
                    />
                  </div>
                  <div className="absolute -bottom-6 -right-6 w-24 h-24 bg-[#D4C4B0] rounded-xl flex items-center justify-center">
                    <i className="ri-double-quotes-r text-[48px] text-black"></i>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-2">
                <div className="flex gap-1 mb-6">
                  {[...Array(testimonials[activeTestimonial].rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-[20px] text-[#D4C4B0]"></i>
                  ))}
                </div>

                <blockquote className="text-[18px] md:text-[20px] lg:text-[22px] text-[#E0E0E0] leading-relaxed mb-8 font-light">
                  "{testimonials[activeTestimonial].quote}"
                </blockquote>

                <div className="border-t border-[#222222] pt-6">
                  <h4 className="text-[20px] font-semibold text-[#F5F5F5] mb-1">
                    {testimonials[activeTestimonial].author}
                  </h4>
                  <p className="text-[14px] text-[#D4C4B0] mb-1">
                    {testimonials[activeTestimonial].role}
                  </p>
                  <p className="text-[13px] text-[#808080]">
                    {testimonials[activeTestimonial].company}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-center gap-3 mt-12">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setActiveTestimonial(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 cursor-pointer ${
                  activeTestimonial === index
                    ? 'bg-[#D4C4B0] w-12'
                    : 'bg-[#333333] hover:bg-[#444444]'
                }`}
                aria-label={`View testimonial ${index + 1}`}
              />
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-20">
          {[
            { number: '500+', label: 'Professionals Trained' },
            { number: '98%', label: 'Client Satisfaction' },
            { number: '15+', label: 'Years Experience' }
          ].map((stat, index) => (
            <div key={index} className="text-center">
              <div className="text-[48px] md:text-[56px] font-bold text-[#D4C4B0] mb-2">
                {stat.number}
              </div>
              <div className="text-[14px] text-[#9A9A9A] uppercase tracking-wider">
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
